import { CantileverButton } from 'components/CantileverButton'
import { X } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  ConsequenceMatrixSelection,
  GeckoRiskAssessmentConfig,
  mapRiskClassToRiskScore,
  RiskCalculationMethod,
} from 'types'

import { computeConsequenceMatrixRisk } from './computeConsequenceMatrixRisk'
import { ConsequenceMatrixSelector } from './ConsequenceMatrixSelector'
import { RiskMatrix } from './RiskMatrix'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div<{ $wide?: boolean }>`
  background: #262626;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: auto;
  max-width: ${({ $wide }) => ($wide ? '90vw' : '30rem')};
  max-height: 80vh;
  overflow-y: auto;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`

const ModalTitle = styled.h3`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`

const LikelihoodSelect = styled.select`
  width: 100%;
  background-color: #363636;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  margin: 1rem 0;

  option {
    background-color: #363636;
    color: #ffffff;
  }
`

const SectionLabel = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
`

const MatricesRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  margin-top: 1rem;
`

const ConsequenceMatrixPanel = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
`

const RiskMatrixPanel = styled.div`
  flex-shrink: 0;
`

interface RiskSelectionModalProps {
  isOpen: boolean
  title: string
  initialLikelihood?: number
  initialConsequence?: number
  initialRiskScore?: number
  initialConsequenceMatrixSelections?: ConsequenceMatrixSelection[]
  riskAssessmentConfig?: GeckoRiskAssessmentConfig
  userId?: string
  userName?: string
  onClose: () => void
  onSave: (
    riskScore: number,
    likelihood: number,
    consequence: number,
    consequenceMatrixSelections?: ConsequenceMatrixSelection[]
  ) => void
}

export const RiskSelectionModal: React.FC<RiskSelectionModalProps> = ({
  isOpen,
  title,
  initialLikelihood,
  initialConsequence,
  initialRiskScore,
  initialConsequenceMatrixSelections,
  riskAssessmentConfig,
  userId,
  userName,
  onClose,
  onSave,
}) => {
  const [tempRisk, setTempRisk] = useState(0)
  const [tempLikelihood, setTempLikelihood] = useState(0)
  const [tempConsequence, setTempConsequence] = useState(0)
  const [tempConsequenceMatrixSelections, setTempConsequenceMatrixSelections] =
    useState<ConsequenceMatrixSelection[]>([])

  const isConsequenceMatrixMethod =
    riskAssessmentConfig?.data.riskCalculationMethod ===
    RiskCalculationMethod.CONSEQUENCE_MATRIX

  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setTempRisk(initialRiskScore ?? 0)
      setTempLikelihood(initialLikelihood ?? 0)
      setTempConsequence(initialConsequence ?? 0)
      setTempConsequenceMatrixSelections(
        initialConsequenceMatrixSelections ?? []
      )
    }
    prevOpenRef.current = isOpen
  }, [
    isOpen,
    initialRiskScore,
    initialLikelihood,
    initialConsequence,
    initialConsequenceMatrixSelections,
  ])

  const likelihoodOptions = useMemo(() => {
    const baseOption = { value: 0, label: 'Select Likelihood' }
    if (riskAssessmentConfig?.data.likelihoodLabels) {
      const configOptions = riskAssessmentConfig.data.likelihoodLabels.map(
        (label, index) => ({ value: index + 1, label: label.description })
      )
      return [baseOption, ...configOptions]
    }
    return [
      baseOption,
      { value: 1, label: 'Rare' },
      { value: 2, label: 'Unlikely' },
      { value: 3, label: 'Possible' },
      { value: 4, label: 'Likely' },
      { value: 5, label: 'Almost Certain' },
    ]
  }, [riskAssessmentConfig?.data.likelihoodLabels])

  useEffect(() => {
    if (!isConsequenceMatrixMethod) return

    const result = computeConsequenceMatrixRisk(
      tempConsequenceMatrixSelections,
      tempLikelihood,
      riskAssessmentConfig
    )

    if (result) {
      setTempRisk(result.riskScore)
      setTempConsequence(result.consequencePosition)
    } else if (tempConsequenceMatrixSelections.length > 0) {
      setTempConsequence(0)
      setTempRisk(0)
    }
  }, [
    isConsequenceMatrixMethod,
    tempConsequenceMatrixSelections,
    tempLikelihood,
    riskAssessmentConfig,
  ])

  const handleSave = () => {
    if (isConsequenceMatrixMethod) {
      onSave(
        tempRisk,
        tempLikelihood,
        tempConsequence,
        tempConsequenceMatrixSelections
      )
    } else {
      onSave(tempRisk, tempLikelihood, tempConsequence)
    }
  }

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent $wide={isConsequenceMatrixMethod}>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        {isConsequenceMatrixMethod &&
        riskAssessmentConfig?.data.consequenceMatrix ? (
          <div>
            <SectionLabel>Likelihood</SectionLabel>
            <LikelihoodSelect
              value={tempLikelihood}
              onChange={(e) => setTempLikelihood(Number(e.target.value))}>
              {likelihoodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </LikelihoodSelect>
            <MatricesRow>
              <ConsequenceMatrixPanel>
                <SectionLabel style={{ marginTop: 0 }}>
                  Consequence Matrix
                </SectionLabel>
                <ConsequenceMatrixSelector
                  config={riskAssessmentConfig.data.consequenceMatrix}
                  selections={tempConsequenceMatrixSelections}
                  onSelectionChange={setTempConsequenceMatrixSelections}
                  userId={userId ?? ''}
                  userName={userName ?? ''}
                />
              </ConsequenceMatrixPanel>
              <RiskMatrixPanel>
                <SectionLabel style={{ marginTop: 0 }}>
                  Risk Matrix
                </SectionLabel>
                <RiskMatrix
                  riskAssessmentConfig={riskAssessmentConfig}
                  selectedLikelihood={
                    tempLikelihood > 0 ? tempLikelihood : undefined
                  }
                  selectedConsequence={
                    tempConsequence > 0 ? tempConsequence : undefined
                  }
                  lockedConsequence={
                    tempConsequence > 0 ? tempConsequence : undefined
                  }
                  onCellClick={(_riskValue, likelihood) => {
                    setTempLikelihood(likelihood)
                  }}
                />
              </RiskMatrixPanel>
            </MatricesRow>
          </div>
        ) : (
          <div>
            <RiskMatrix
              riskAssessmentConfig={riskAssessmentConfig}
              selectedLikelihood={
                tempLikelihood > 0 ? tempLikelihood : undefined
              }
              selectedConsequence={
                tempConsequence > 0 ? tempConsequence : undefined
              }
              onCellClick={(riskValue, likelihood, consequence) => {
                const riskScore = mapRiskClassToRiskScore(
                  riskValue,
                  riskAssessmentConfig
                )
                setTempRisk(riskScore)
                setTempLikelihood(likelihood)
                setTempConsequence(consequence)
              }}
            />
          </div>
        )}

        <ModalActions>
          <CantileverButton onClick={onClose}>Cancel</CantileverButton>
          <CantileverButton
            primary
            disabled={
              tempRisk === 0 || tempLikelihood <= 0 || tempConsequence <= 0
            }
            onClick={handleSave}>
            Save
          </CantileverButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  )
}
